// src/BFSI.js
import React, { useState, useContext, useMemo } from "react";
import "./BFSI.css";
import { ChatContext } from "../ChatContext";
import { useStrings } from "../strings/StringsContext";

// base path for CRA public assets
const base = process.env.PUBLIC_URL || "";

// Local safe defaults if JSON doesn’t provide page prompts
const DEFAULT_PROMPTS = [
  { title: "Transaction dispute", content: "List of upcoming financial regulations and their impact." },
  { title: "Credit report dispute", content: "Summary of the latest financial market trends and insights." },
  { title: "Personal data mismatch", content: "Recommended clients to meet next based on priorities." },
  { title: "Analyze competitors", content: "Comparative analysis of competitor offerings and advantages." },
  { title: "Show me declining accounts", content: "Here are the accounts showing declining activity." },
  { title: "Pending loan applications", content: "List of pending loan applications assigned to you." },
];

// ✅ build correct URLs to /public assets
const imageCycle = [
  `${base}/bfsisample1.jpg`,
  `${base}/bfsisample2.jpg`,
  `${base}/bfsisample3.jpg`,
];

export default function BFSI() {
  const { strings, agentName } = useStrings();

  const { setTriggeredPrompt, addMessage, setChatState } = useContext(ChatContext);
  const [searchText, setSearchText] = useState("");

  // Page-level strings (with graceful fallbacks)
  const pageCfg = strings?.pages?.bfsi || {};
  const greeting =
    pageCfg.greeting || `Good morning! How may ${agentName} help you?`;
  const searchPlaceholder =
    strings?.search_placeholder || `Ask ${agentName} anything…`;
  const searchAria =
    strings?.search_aria || `Ask ${agentName} anything`;
  const searchBtnAria =
    strings?.search_button_aria || `Send to ${agentName}`;
  const announcementsTitle =
    pageCfg.announcements_title || "Announcements";
  const announcementsDate =
    pageCfg.announcements_date || "July 28, 2025"; // sample date; customize via JSON if needed

  // Prompts from JSON (falls back to local defaults)
  const rawPrompts = pageCfg.prompts || DEFAULT_PROMPTS;

  const cardsToShow = useMemo(
    () =>
      rawPrompts.map((prompt, idx) => ({
        ...prompt,
        image: imageCycle[idx % imageCycle.length],
      })),
    [rawPrompts]
  );

  const sendSearchToChatbot = (raw) => {
    const text = (raw || "").trim();
    if (!text) return;

    setTriggeredPrompt(text);
    addMessage({
      type: "user",
      text,
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    });

    setChatState((prev) => ({ ...prev, isOpen: true, isExpanded: true }));
    setSearchText("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendSearchToChatbot(searchText);
  };

  return (
    <div className="bfsi-dashboard">
      <div className="header-section">
        <h1>{greeting}</h1>

        {/* ✅ use PUBLIC_URL for public assets */}
        <img
          src={`${base}/agentforce-flying.png`}
          alt={`${agentName} illustration`}
          className="agentforce-flying"
        />

        {/* Search bar */}
        <form className="search-bar" onSubmit={handleSubmit} role="search" aria-label={searchAria}>
          <img src={`${base}/bot-avatar.png`} alt="" aria-hidden="true" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            aria-label={searchAria}
          />
          <button
            type="submit"
            className="search-send-button"
            aria-label={searchBtnAria}
            disabled={!searchText.trim()}
          >
            <span className="arrow-icon">➤</span>
          </button>
        </form>

        {/* Prompt buttons */}
        <div className="prompt-buttons" role="group" aria-label="Quick prompts">
          {rawPrompts.map((prompt, index) => (
            <button
              key={index}
              className="prompt-button"
              onClick={() => sendSearchToChatbot(prompt.title)}
              type="button"
            >
              {prompt.title}
            </button>
          ))}
        </div>
      </div>

      {/* Announcements section */}
      <div className="announcement-section">
        <h2>{announcementsTitle}</h2>
        <div className="announcement-grid">
          {cardsToShow.slice(0, 6).map((card, index) => (
            <div className="announcement-card" key={index}>
              <img className="card-top-image" src={card.image} alt={card.title} />
              <div className="card-content">
                <div className="date">{announcementsDate}</div>
                <div className="title">{card.title}</div>
                <div className="description">{card.content}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
